/**
 * UpcomingPaymentsTab Component
 * Displays upcoming payments with date filter controls
 */

import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/validation';
import ContractReferenceCell from '../contracts/ContractReferenceCell';
import SortableHeaderCell from '../common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';
import usePagination from '../../hooks/usePagination';
import { investorLabel, partialPaymentInfo, groupScheduleRowsByContractDate, applyRowFilters } from '../../utils/paymentHelpers';
import RowFilterChips from './RowFilterChips';

const UPCOMING_COLUMNS = [
  { key: 'contract',      align: 'left',  getValue: (p) => p.contract_reference || p.reference },
  // T3-109: sort on the resolved LABEL, not the raw name — a multi-investor row
  // has no investor_name, so sorting on it left those rows in an arbitrary blank
  // cluster instead of under "Multiple".
  { key: 'investor',      align: 'left',  getValue: (p) => investorLabel(p) },
  { key: 'due_date',      align: 'left',  getValue: (p) => p.due_date || p.payment_date },
  { key: 'type',          align: 'left',  getValue: (p) => p.payment_type },
  { key: 'amount',        align: 'right', getValue: (p) => p.expected_amount != null ? parseFloat(p.expected_amount) : (p.amount != null ? parseFloat(p.amount) : null) },
  // T3-122: the residual rolled in from an earlier short payment — sortable so a reviewer can
  // find every line carrying one.
  { key: 'carried_in',   align: 'right', getValue: (p) => parseFloat(p.residual_carried_in) || 0 },
  // T3-111: sort on the same figures the three money columns display.
  { key: 'allocated',     align: 'right', getValue: (p) => partialPaymentInfo(p).matched },
  { key: 'outstanding',   align: 'right', getValue: (p) => partialPaymentInfo(p).outstanding },
  { key: 'status',        align: 'left',  getValue: (p) => p.status || p.payment_status },
];

function UpcomingPaymentsTab({
  payments = [],
  currencyCode = 'ZAR',
  showContractColumn = true,
  showInvestorColumn = false,
  showMatchActions = true,
  onMatch,
  filter = '30days',
  onFilterChange,
  customDate,
  onCustomDateChange,
  investorNameField = 'investor_name',
  showFilter = true,
  // Opt-in so the ContractDetail reuse is unchanged. Payment Matching passes it:
  // with the horizon set to All these queues list every future unmatched row
  // (hundreds per tenant on dev), where previously they rendered every row into
  // a single table with no pagination at all.
  paginated = false,
  // T3-109 opt-in: collapse a multi-investor contract's per-investor rows into ONE
  // matchable line (expandable to the individual rows). Payment Matching passes it;
  // ContractDetail deliberately does not — there the per-investor breakdown IS the content.
  groupMultiInvestor = false,
}) {
  const sort = useTableSort();
  const pg = usePagination({ defaultRowsPerPage: 25 });
  // Composition chips (Single / Multiple / Partially Paid). Empty = no filter, never "no rows".
  const [activeFilters, setActiveFilters] = React.useState([]);
  // T3-109: group BEFORE sorting and paginating — grouping after pagination could split one
  // contract's investors across two pages, and sorting raw rows would interleave a group's
  // members with other contracts.
  const groupedPayments = groupMultiInvestor
    ? groupScheduleRowsByContractDate(payments)
    : payments;
  // Composition chips filter AFTER grouping, so the counts on the chips match the lines on screen.
  const filteredPayments = applyRowFilters(groupedPayments, activeFilters);
  const sortedPayments = sort.sortRows(filteredPayments, UPCOMING_COLUMNS);
  const visiblePayments = paginated ? pg.paginateData(sortedPayments) : sortedPayments;

  // Which grouped contract lines are expanded to show their per-investor rows.
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const toggleGroup = (key) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  /**
   * One table row — shared by the group line, an ordinary ungrouped row, and an expanded
   * per-investor member row, so the columns are defined ONCE. A member row that drifted from
   * the line above it would be actively misleading.
   */
  const renderPaymentRow = ({ payment, isGroup, isMember = false, onToggle, expanded, key }) => {
    const { isPartial, outstanding, matched } = partialPaymentInfo(payment);
    const carriedIn = parseFloat(payment.residual_carried_in) || 0;
    return (
      <TableRow
        key={key}
        hover
        onClick={showMatchActions && onMatch ? () => onMatch(payment) : undefined}
        sx={{
          ...(showMatchActions && onMatch ? { cursor: 'pointer' } : {}),
          ...(isMember ? { backgroundColor: 'action.hover' } : {}),
        }}
      >
        {showContractColumn && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: isMember ? 3 : 0 }}>
              {isGroup && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onToggle(); }}
                  aria-label={expanded ? 'Hide individual investors' : 'Show individual investors'}
                  sx={{ p: 0.25 }}
                >
                  {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              )}
              {isMember ? (
                <Typography variant="caption" color="textSecondary">↳</Typography>
              ) : (
                <ContractReferenceCell reference={payment.contract_reference || '-'} userReference={payment.user_reference} />
              )}
              {/* T3-059: matured-but-not-yet-finalised contracts stay matchable here
                  (Path A). Flag them so users know it's post-maturity. */}
              {!isMember && payment.contract_status === 'Matured' && (
                <Chip label="Matured" size="small" color="secondary" variant="outlined" />
              )}
            </Box>
          </TableCell>
        )}
        {showInvestorColumn && (
          <TableCell>
            {/* T3-109: fall back to the "Multiple (N)" label rather than a bare
                dash. investorNameField is kept because other callers point this
                column at a different field; the resolver only supplies the
                fallback when that field is empty. The tooltip carries the actual
                names so the row is identifiable without expanding anything. */}
            <Tooltip title={(!payment[investorNameField] && payment.investor_names) || ''}>
              <Typography variant="body2">
                {payment[investorNameField] || investorLabel(payment)}
              </Typography>
            </Tooltip>
          </TableCell>
        )}
        <TableCell>
          {payment.payment_date
            ? format(new Date(payment.payment_date), 'dd MMM yyyy')
            : '-'}
        </TableCell>
        <TableCell>
          <Chip
            label={payment.narration || 'Payment'}
            size="small"
            variant="outlined"
            color={
              payment.narration?.includes('Interest') ? 'info' :
              payment.narration?.includes('Capital') ? 'success' : 'default'
            }
          />
        </TableCell>
        {/* T3-111: three explicit money columns rather than one figure with a strike-through.
            Amount / Allocated / Outstanding is the standard accounting presentation, and it states
            what a line-through only implied — on a part-paid line the scheduled amount is NOT what
            is owed. */}
        <TableCell align="right">
          <Typography variant="body2" color={isPartial ? 'textSecondary' : 'inherit'}>
            {payment.currency_code || currencyCode} {formatNumber(payment.payment_amount)}
          </Typography>
        </TableCell>
        {/* T3-122: how much of Amount was rolled in from an EARLIER short payment. The Amortization
            tab has always shown this as a "+R" chip; the queue was silent, so a line read higher
            than the contract's own schedule for that period with nothing explaining the gap.
            '-' when zero, like Allocated, so it reads as "nothing to say". */}
        <TableCell align="right">
          {carriedIn > 0.005 ? (
            <Tooltip title={`Includes R ${formatNumber(carriedIn)} rolled in from a prior override`}>
              <Typography variant="body2" color="info.main">{formatNumber(carriedIn)}</Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="textSecondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={matched > 0 ? 'success.main' : 'textSecondary'}>
            {matched > 0 ? formatNumber(matched) : '-'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography fontWeight="medium" color={isPartial ? 'warning.main' : 'inherit'}>
            {formatNumber(outstanding)}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip label="Pending" size="small" variant="outlined" />
            {isPartial && <Chip label="Part paid" size="small" variant="outlined" color="warning" />}
            {/* T3-121: the shortfall on this line has been submitted for approval to move to the
                next payment. The line is STILL outstanding until that is applied, so it correctly
                stays in the queue — this says why, at the point someone asks. */}
            {(payment.carry_forward_pending === true || payment.carry_forward_pending === 'true') && (
              <Tooltip title={`${formatNumber(payment.carry_forward_amount)} submitted for approval to carry to the next payment`}>
                <Chip label="Carry-forward pending" size="small" variant="outlined" color="info" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        {showMatchActions && (
          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
            {onMatch && (
              <Button
                variant={isGroup ? 'contained' : 'outlined'}
                size="small"
                onClick={() => onMatch(payment)}
              >
                Match
              </Button>
            )}
          </TableCell>
        )}
      </TableRow>
    );
  };

  // Snap back to page 1 when the set shrinks past the current page — narrowing
  // the horizon (All -> 30d) would otherwise strand the user on a blank table.
  const { page, rowsPerPage, resetPage } = pg;
  useEffect(() => {
    if (paginated && page > 0 && page * rowsPerPage >= sortedPayments.length) resetPage();
  }, [paginated, page, rowsPerPage, sortedPayments.length, resetPage]);

  return (
    <Box>
      {groupMultiInvestor && (
        <RowFilterChips
          rows={groupedPayments}
          active={activeFilters}
          onChange={(next) => { setActiveFilters(next); pg.resetPage(); }}
          sx={{ mb: 1.5 }}
        />
      )}
      {/* Filter controls */}
      {showFilter && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Show</InputLabel>
            <Select
              value={filter}
              onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
              label="Show"
            >
              <MenuItem value="30days">Next 30 Days</MenuItem>
              <MenuItem value="90days">Next 90 Days</MenuItem>
              <MenuItem value="all">All Future</MenuItem>
              <MenuItem value="custom">Custom Date</MenuItem>
            </Select>
          </FormControl>

          {filter === 'custom' && (
            <TextField
              type="date"
              size="small"
              value={customDate ? format(customDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => onCustomDateChange && onCustomDateChange(e.target.value ? new Date(e.target.value) : null)}
              InputLabelProps={{ shrink: true }}
              label="Show until"
              sx={{ minWidth: 180 }}
            />
          )}

          <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
            {payments.length} payment{payments.length !== 1 ? 's' : ''} in selected period
          </Typography>
        </Box>
      )}

      {payments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="textSecondary" fontWeight="medium" gutterBottom>
            No upcoming payments in selected period
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try selecting a longer time period to see more payments
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {showContractColumn && <SortableHeaderCell sort={sort} columnKey="contract">Contract</SortableHeaderCell>}
                {showInvestorColumn && <SortableHeaderCell sort={sort} columnKey="investor">Investor</SortableHeaderCell>}
                <SortableHeaderCell sort={sort} columnKey="due_date">Due Date</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="type">Type</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="carried_in" align="right">Carried in</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="allocated" align="right">Allocated</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="outstanding" align="right">Outstanding</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="status">Status</SortableHeaderCell>
                {showMatchActions && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* A filter that empties the table must SAY it is a filter — otherwise an empty grid
                  reads as "nothing is due", which is the opposite of the truth. */}
              {visiblePayments.length === 0 && activeFilters.length > 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      No payments match the selected filters — {payments.length} row(s) are hidden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visiblePayments.map((payment, index) => {
                const isGroup = payment.isGroup === true;
                const rowKey = payment._groupKey || payment.id || `upcoming-${index}`;
                const expanded = isGroup && !!expandedGroups[rowKey];
                return (
                  <React.Fragment key={rowKey}>
                    {renderPaymentRow({
                      payment,
                      isGroup,
                      onToggle: isGroup ? () => toggleGroup(rowKey) : undefined,
                      expanded,
                    })}
                    {expanded && payment.members.map((m) => renderPaymentRow({
                      payment: m,
                      isGroup: false,
                      isMember: true,
                      key: `member-${m.id}`,
                    }))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          {paginated && <TablePagination {...pg.tableProps(sortedPayments.length)} />}
        </TableContainer>
      )}
    </Box>
  );
}

export default UpcomingPaymentsTab;
